import { LandingFooter, LandingNav } from '~/app/landing/landing-chrome';
import { LandingShell } from '~/app/landing/landing-shell';
import PageSpinner from '~/components/page-spinner';
import SEO from '~/components/seo';
import { CANONICAL_ORIGIN } from '~/constants';
import { useLanguageContext } from '~/contexts/language-context';
import { useRouter } from 'next/router';
import React from 'react';
import urlJoin from 'url-join';

interface PolicyResponse {
  html: string;
  data?: {
    title?: string;
  };
}

const PolicyPage: React.FC = () => {
  let router = useRouter();
  let { language } = useLanguageContext();
  let [policy, setPolicy] = React.useState<PolicyResponse | null>(null);
  let [error, setError] = React.useState(false);

  let policyName = React.useMemo(() => {
    let value = router.query.policy;
    if (!value) {
      return null;
    }
    return Array.isArray(value) ? value.join('-') : value;
  }, [router.query.policy]);

  let canonicalUrl = policyName ? urlJoin(CANONICAL_ORIGIN, `/policies/${policyName}`) : undefined;

  React.useEffect(() => {
    if (!policyName) {
      return;
    }

    let cancelled = false;
    setPolicy(null);
    setError(false);

    fetch(`/api/policy/${encodeURIComponent(policyName)}?language=${encodeURIComponent(language)}`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Failed to load policy');
        }
        return response.json();
      })
      .then((data: PolicyResponse) => {
        if (!cancelled) {
          setPolicy(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [language, policyName]);

  if (!policyName || (!policy && !error)) {
    return <PageSpinner />;
  }

  return (
    <LandingShell className="min-h-screen flex flex-col landing-cream">
      <SEO title={error ? 'Policy' : policy?.data?.title || 'Policy'} canonicalUrl={canonicalUrl} />

      <LandingNav />

      <main className="flex-1 py-12">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          {error || !policy ? (
            <p className="landing-muted">Policy not found.</p>
          ) : (
            <div className="rounded-2xl border border-[#e2e1de] bg-white p-8 sm:p-10 shadow-sm">
              <div className="md-content" dangerouslySetInnerHTML={{ __html: policy.html }} />
            </div>
          )}
        </div>
      </main>

      <LandingFooter />
    </LandingShell>
  );
};

export default PolicyPage;
